import type { MessageBlock, TaskPlanUpdate, ThreadMessage } from "@rakazo/contracts";

export type ResponseAttachment = { messageId: string; blocks: readonly MessageBlock[] };

export function taskPlanFromBlocks(blocks: readonly MessageBlock[]): TaskPlanUpdate | null {
  const block = blocks.find((item) => item.kind === "steps" && item.plan);
  if (block?.kind !== "steps" || !block.summary) return null;
  return {
    summary: block.summary,
    steps: block.steps.map((step, index) => ({
      id: step.id ?? String(index),
      label: step.label,
      status: step.status ?? "pending",
    })),
  };
}

/** Presentation only: original records, signatures, and reply targets are never changed. */
export function projectChatWork(
  messages: readonly ThreadMessage[],
  protectedIds: ReadonlySet<string> = new Set(),
) {
  const hidden = new Set<string>();
  const attachments = new Map<string, ResponseAttachment[]>();
  const referenced = new Set([
    ...protectedIds,
    ...messages.flatMap((m) => (m.replyToMessageId ? [m.replyToMessageId] : [])),
  ]);
  const newestPlans = new Map<string, ThreadMessage>();
  const isPlan = (m: ThreadMessage) =>
    m.blocks.length > 0 && m.blocks.every((b) => b.kind === "steps" && b.plan);
  for (const m of messages) {
    if (m.deleted || m.role !== "bot" || !m.runId || !isPlan(m)) continue;
    const key = `${m.threadId}:${m.botId}:${m.runId}`;
    const old = newestPlans.get(key);
    if (old && !referenced.has(old.id)) hidden.add(old.id);
    newestPlans.set(key, m);
  }
  let segment: ThreadMessage[] = [];
  const flush = () => {
    const target = segment.findLast(
      (m) =>
        !m.deleted &&
        !m.replyToMessageId &&
        m.blocks.some((b) => b.kind === "text") &&
        m.blocks.every((b) => ["text", "progress", "steps", "file", "image"].includes(b.kind)),
    );
    if (target) {
      const files = segment
        .slice(0, segment.indexOf(target))
        .filter(
          (m) =>
            !m.deleted &&
            !m.replyToMessageId &&
            !referenced.has(m.id) &&
            m.blocks.length > 0 &&
            m.blocks.every((b) => b.kind === "file" || b.kind === "image"),
        );
      if (files.length) {
        attachments.set(
          target.id,
          files.map((m) => ({ messageId: m.id, blocks: m.blocks })),
        );
        files.forEach((m) => hidden.add(m.id));
      }
    }
    segment = [];
  };
  for (const m of messages) {
    const prior = segment.at(-1);
    if (
      m.role !== "bot" ||
      !m.runId ||
      m.deleted ||
      (prior &&
        (m.runId !== prior.runId || m.botId !== prior.botId || m.threadId !== prior.threadId)) ||
      m.blocks.some((b) =>
        ["ask", "mcp_approval", "app_connect", "choice", "channel_message"].includes(b.kind),
      )
    ) {
      flush();
      if (
        m.role !== "bot" ||
        !m.runId ||
        m.deleted ||
        m.blocks.some((b) =>
          ["ask", "mcp_approval", "app_connect", "choice", "channel_message"].includes(b.kind),
        )
      )
        continue;
    }
    segment.push(m);
  }
  flush();
  return { visibleMessages: messages.filter((m) => !hidden.has(m.id)), attachments };
}

export function liveActivityDetail(
  messages: readonly { id: string; blocks: readonly MessageBlock[] }[],
): string | undefined {
  const tail = messages.findLast((m) => m.id.startsWith("progress:"))?.blocks.at(-1);
  return tail?.kind === "progress" && tail.activity ? tail.detail : undefined;
}
